import Header from "../../components/feature/Header";
import Footer from "../../components/feature/Footer";
import SEO from "../../components/base/SEO";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

const evaluationQuestions = [
  "你的主要痛點是庫存準確、執貨效率、派車效率，還是客戶查詢壓力？",
  "系統是否支援香港常見流程，例如 HKTVmall / Shopify 訂單、快遞單、POD、司機 WhatsApp 溝通？",
  "是否有本地團隊理解香港倉庫、商廈、車隊、司機和最後一公里配送場景？",
  "報表、欄位、POD、失敗原因、客戶入口和 API 對接能否按你的流程調整？",
  "上線後是否有人協助培訓倉庫同事、調度員和司機，而不只是交一套系統給你？",
];

const fitMatrix = [
  {
    title: "適合先上 WMS 的公司",
    points: [
      "庫存經常不準，Excel 與實物數量對不上",
      "電商平台訂單多，Shopify、HKTVmall、自家網店庫存容易超賣",
      "倉庫揀貨、包裝、覆核主要靠人手記錄",
      "3PL 要管理多客戶、多 SKU、多倉位和增值服務",
    ],
    cta: "了解 WMS",
    href: "/wms/ecommerce",
  },
  {
    title: "適合先上 TMS 的公司",
    points: [
      "每日要排多區送貨，Excel 排車和 WhatsApp 調度開始失控",
      "客戶經常追問 ETA、送貨狀態和 POD 簽收證明",
      "需要管理司機、車輛、路線、送貨成本和準時率",
      "想用 AI 排線、即時追蹤和客戶 tracking link 提升服務形象",
    ],
    cta: "了解 TMS",
    href: "/tms",
  },
  {
    title: "適合同時整合 WMS + TMS 的公司",
    points: [
      "由訂單、庫存、執貨、出貨到配送都想打通",
      "倉庫與車隊經常因資料不同步而互相追問",
      "想讓客戶從下單、出貨到簽收都能看到一致狀態",
      "管理層需要從同一套數據看倉庫效率、配送效率和成本",
    ],
    cta: "看整合方案",
    href: "/",
  },
];

const comparisonRows = [
  ["核心目標", "提升庫存準確、揀貨效率、倉庫作業可追蹤", "提升派車效率、配送透明度、司機和車輛管理"],
  ["主要使用者", "倉務、客服、營運、3PL 客戶服務團隊", "調度員、司機、車隊主管、客戶服務團隊"],
  ["典型功能", "入庫、上架、庫存、揀貨、包裝、快遞單、盤點", "接單、排線、派車、司機 App、POD、即時追蹤、報表"],
  ["香港常見整合", "Shopify、HKTVmall、WooCommerce、快遞公司、ERP", "地址解析、客戶入口、tracking link、POD、API 落單"],
  ["最容易低估", "倉位規則、例外處理、平台庫存同步、現場培訓", "司機採用率、商廈限制、實際服務時間、插單處理"],
];

const faqItems = [
  {
    question: "香港公司應該先導入 WMS 還是 TMS？",
    answer:
      "如果問題主要在庫存、揀貨和倉庫效率，先上 WMS；如果問題主要在排車、追車、POD 和客戶查詢，先上 TMS。若你同時有倉庫和自家車隊，WMS + TMS 整合通常能帶來更完整的營運可視性。",
  },
  {
    question: "選香港 WMS / TMS vendor 時，最重要看什麼？",
    answer:
      "不要只看 demo 介面和首年價錢。更重要是本地流程理解、可調整能力、API 整合能力、前線培訓，以及供應商是否理解香港倉庫、商廈、司機和最後一公里配送限制。",
  },
  {
    question: "AUTOCHAIN 適合哪些香港企業？",
    answer:
      "AUTOCHAIN 適合電商倉、3PL、批發零售、配送車隊，以及需要把倉庫管理和運輸管理數碼化的香港企業。系統特別針對香港本地操作、廣東話支援和實際物流流程設計。",
  },
  {
    question: "WMS / TMS 導入通常需要準備什麼？",
    answer:
      "你需要整理 SKU、庫存、客戶、車輛、司機、送貨區域、常用報表和現有流程。好的 vendor 應該協助你把現場流程轉化成系統設定，而不是要求公司完全遷就系統。",
  },
];

export default function WMSTMSSelectionGuide() {
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "首頁",
        item: "https://autochain.com.hk/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "香港 WMS / TMS 系統選型指南",
        item: "https://autochain.com.hk/wms-tms-selection-guide",
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="香港 WMS / TMS 系統選型指南 | AUTOCHAIN"
        description="香港企業選 WMS 倉庫管理系統或 TMS 運輸管理系統時，應如何判斷需求、比較 vendor、評估本地支援與整合能力？AUTOCHAIN 整理一份香港物流企業選型指南。"
        keywords="香港WMS, 香港TMS, WMS系統選型, TMS系統選型, 倉庫管理系統比較, 車隊管理系統比較, 物流系統供應商, WMS vendor, TMS vendor, AUTOCHAIN"
        canonicalUrl="/wms-tms-selection-guide"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbJsonLd)}
        </script>
      </Helmet>
      <Header />

      <main>
        <section className="bg-slate-950 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
            <div className="max-w-4xl">
              <p className="text-sm font-semibold tracking-wide text-cyan-300 uppercase">
                Hong Kong WMS / TMS Selection Guide
              </p>
              <h1 className="mt-5 text-4xl md:text-6xl font-bold leading-tight">
                香港 WMS / TMS 系統選型指南
              </h1>
              <p className="mt-6 text-xl text-slate-200 leading-relaxed">
                給正在比較倉庫管理系統、車隊管理系統、送貨系統和物流
                vendor 的香港企業：先釐清問題，再選系統，才不會買到
                demo 好看但落地困難的方案。
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/wms/ecommerce"
                  className="inline-flex items-center px-5 py-3 rounded-lg bg-cyan-400 text-slate-950 font-semibold hover:bg-cyan-300 transition-colors"
                >
                  看 WMS 方案
                </Link>
                <Link
                  to="/tms"
                  className="inline-flex items-center px-5 py-3 rounded-lg border border-white/30 text-white font-semibold hover:bg-white/10 transition-colors"
                >
                  看 TMS 方案
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <h2 className="text-3xl font-bold text-slate-900">
                先判斷你真正要解決的問題
              </h2>
              <p className="mt-4 text-lg text-slate-600 leading-relaxed">
                WMS 和 TMS 都是物流系統，但解決的問題不同。WMS 管倉庫、
                庫存、揀貨和出貨；TMS 管排車、司機、路線、POD 和配送狀態。
                對香港公司來說，重點不是功能表有多長，而是系統是否貼合本地
                倉庫和最後一公里操作。
              </p>
            </div>

            <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
              {fitMatrix.map((item) => (
                <article
                  key={item.title}
                  className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm"
                >
                  <h3 className="text-xl font-bold text-slate-900">{item.title}</h3>
                  <ul className="mt-5 space-y-3">
                    {item.points.map((point) => (
                      <li key={point} className="flex gap-3 text-slate-700">
                        <span className="mt-1 h-2 w-2 rounded-full bg-cyan-500 flex-none" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={item.href}
                    className="mt-6 inline-flex text-blue-700 font-semibold hover:text-blue-900"
                  >
                    {item.cta}
                  </Link>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <h2 className="text-3xl font-bold text-slate-900">
                WMS 和 TMS 的差異
              </h2>
              <p className="mt-4 text-lg text-slate-600">
                以下比較適合香港電商、3PL、批發零售和配送車隊用作初步選型。
              </p>
            </div>

            <div className="mt-10 overflow-x-auto border border-slate-200 rounded-lg">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-900 text-white">
                  <tr>
                    <th className="px-5 py-4 font-semibold">比較項目</th>
                    <th className="px-5 py-4 font-semibold">WMS 倉庫管理系統</th>
                    <th className="px-5 py-4 font-semibold">TMS 運輸管理系統</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {comparisonRows.map(([label, wms, tms]) => (
                    <tr key={label} className="bg-white">
                      <td className="px-5 py-4 font-semibold text-slate-900">{label}</td>
                      <td className="px-5 py-4 text-slate-700">{wms}</td>
                      <td className="px-5 py-4 text-slate-700">{tms}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="py-16 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              <div>
                <h2 className="text-3xl font-bold text-slate-900">
                  問 vendor 前，先準備這 5 條問題
                </h2>
                <p className="mt-4 text-lg text-slate-600 leading-relaxed">
                  這些問題能幫你分辨一套系統是否真的適合香港，而不只是
                  demo 畫面漂亮。
                </p>
              </div>
              <ol className="space-y-4">
                {evaluationQuestions.map((question, index) => (
                  <li
                    key={question}
                    className="flex gap-4 bg-white border border-slate-200 rounded-lg p-5"
                  >
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-700 text-white text-sm font-bold flex-none">
                      {index + 1}.
                    </span>
                    <span className="text-slate-800 font-medium">{question}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <h2 className="text-3xl font-bold text-slate-900">
                常見問題
              </h2>
            </div>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              {faqItems.map((item) => (
                <article
                  key={item.question}
                  className="border border-slate-200 rounded-lg p-6"
                >
                  <h3 className="text-lg font-bold text-slate-900">
                    {item.question}
                  </h3>
                  <p className="mt-3 text-slate-600 leading-relaxed">{item.answer}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 bg-blue-700 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
            <div className="max-w-3xl">
              <h2 className="text-3xl font-bold">
                想知道你的公司應該先上 WMS、TMS，還是一起整合？
              </h2>
              <p className="mt-4 text-blue-100 text-lg">
                AUTOCHAIN 可以按你現有倉庫、車隊、訂單平台和客戶流程，協助你拆解導入優先次序。
              </p>
            </div>
            <a
              href="https://wa.me/85294252903?text=%E6%82%A8%E5%A5%BD%EF%BC%8C%E6%88%91%E6%83%B3%E4%BA%86%E8%A7%A3%20AUTOCHAIN%20WMS%20/%20TMS%20%E9%81%B8%E5%9E%8B%E5%BB%BA%E8%AD%B0%E3%80%82"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-white text-blue-700 font-bold hover:bg-blue-50 transition-colors"
            >
              WhatsApp 查詢
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
